import Box from '@mui/material/Box';
import FormHelperText from '@mui/material/FormHelperText';
import FormLabel from '@mui/material/FormLabel';
import Link from '@mui/material/Link';
import Typography from '@mui/material/Typography';
import { ReactElement, useCallback, useEffect, useMemo } from 'react';
import { Control, useController, UseFormSetValue } from 'react-hook-form';

import CategoryCheckboxLabel from '../categories/CategoryCheckboxLabel';
import CheckboxGroup from '../forms/CheckboxGroup';
import CategoryDto from '../../dto/categories/category.dto';
import BookingPreferenceFieldValuesInterface from '../../interfaces/booking-preferences/booking-preference-field-values.interface';
import { PATHS } from '../../utils/paths';
import { validateArrayRequired } from '../../utils/validations';

interface Props {
  categories: CategoryDto[];
  control: Control<BookingPreferenceFieldValuesInterface>;
  edited: boolean;
  sectionId: string;
  setValue: UseFormSetValue<BookingPreferenceFieldValuesInterface>;
}

const SectionCategoriesForm = ({
  categories,
  control,
  edited,
  sectionId,
  setValue,
}: Props): ReactElement => {
  const {
    field: { onChange, value },
    fieldState: { error },
  } = useController({
    name: 'categories',
    rules: validateArrayRequired(1, 3),
    control,
  });

  const hasSingleCategory = useMemo(
    () => categories.length === 1,
    [categories],
  );

  useEffect(() => {
    if (!edited) return;

    const value = hasSingleCategory ? [categories[0].id] : [];

    setValue('categories', value);
  }, [categories, edited, hasSingleCategory, sectionId, setValue]);

  const isDisabled = useCallback(
    (selection: string[], category: string): boolean => {
      return (
        hasSingleCategory ||
        (!selection.includes(category) && selection.length >= 3)
      );
    },
    [hasSingleCategory],
  );

  const options = useMemo(() => {
    return categories.map((category) => {
      const { id, closed } = category;

      return {
        label: <CategoryCheckboxLabel category={category} />,
        value: id,
        disabled: closed,
      };
    });
  }, [categories]);

  return (
    <Box sx={{ pt: 4 }}>
      <FormLabel>
        Catégorie(s) d&apos;activité(s) du secteur sélectionné
        <Typography variant="body2" component="p">
          Choisir 1 à 3 catégories correspondant à votre entreprise.
        </Typography>
        <Typography variant="body2" component="p">
          Notez qu&apos;afin de maintenir la diversité nécessaire au succès de
          l&apos;évènement une{' '}
          <Link target="_blank" href={PATHS.documents.squareFeetPolicy.path}>
            limite d&apos;espaces par secteur d&apos;activité a été établie.
          </Link>
        </Typography>
        {!!error && <FormHelperText error>{error.message}</FormHelperText>}
      </FormLabel>

      <CheckboxGroup
        sx={{
          backgroundColor: 'grey.200',
          display: 'block',
          padding: '15px 20px 10px 20px !important',
          marginBottom: '20px',
          mt: '20px !important',
        }}
        disabled={isDisabled}
        error={error}
        onChange={(value): void => onChange(value as string[])}
        options={options}
        value={value}
        row
      />
    </Box>
  );
};

export default SectionCategoriesForm;
